import { ReactNode } from 'react';

import { Box, Button, DialogActions, DialogContent, PaperProps, Typography, useTheme } from '@mui/material';
import { noop } from 'lodash';
import { useTranslation } from 'next-i18next';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';

export enum ModalVariant {
  WARNING = 'warning',
}

type BaseModalWrapperProps = {
  title: string;
  description: string;
  children?: ReactNode | ReactNode[];
  disabledBackDrop?: boolean;
  disabledEscapeKeyDown?: boolean;
  hideCloseButton?: boolean;
  variant?: ModalVariant;
  customIcon?: string;
  open?: boolean;
  confirmText?: string;
  cancelText?: string;
  fullWidthConfirmButton?: boolean;
  paperStyle?: Partial<PaperProps['sx']>;
  onClose?: typeof noop;
  onCancel?: typeof noop;
  onConfirm?: typeof noop;
};

const DEFAULT_CONFIRM_TEXT = 'ตกลง';
const DEFAULT_CANCEL_TEXT = 'ยกเลิก';

const BaseModalAlertWrapper = ({
  onClose,
  variant,
  children,
  customIcon,
  open = false,
  disabledBackDrop = false,
  disabledEscapeKeyDown = true,
  hideCloseButton = false,
  fullWidthConfirmButton = false,
  title,
  description,
  paperStyle,
  confirmText,
  cancelText,
  onCancel,
  onConfirm,
}: BaseModalWrapperProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClose = () => {
    onClose?.();
  };

  const handleCancel = () => {
    onClose?.();
    onCancel?.();
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const iconStatus = {
    warning: 'fa-light fa-circle-exclamation',
    error: 'fa-light fa-circle-xmark',
    success: 'fa-solid fa-circle-check',
  };

  const iconColor = {
    warning: theme.palette.textV2.disabled,
    error: theme.palette.errorV2.main,
    success: theme.palette.success.main,
  };

  const isOnCancel = typeof onCancel === 'function' || !!cancelText;

  return (
    <BaseModalWrapper
      open={open}
      disabledBackDrop={disabledBackDrop}
      disabledEscapeKeyDown={disabledEscapeKeyDown}
      hideCloseButton={hideCloseButton}
      onClose={handleClose}
      paperStyle={paperStyle}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <DialogContent
          sx={{
            px: {
              xs: 0,
              md: 2,
            },
            pb: 0,
            overflow: 'unset',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: children ? '8px' : '24px',
                alignItems: 'center',
              }}
            >
              {!!variant && (
                <FontAwesomeIcon
                  style={{ fontSize: '48px' }}
                  color={iconColor[variant]}
                  icon={customIcon || iconStatus[variant]}
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}
                  color={theme.palette.secondaryCPD.main}
                >
                  {title}
                </Typography>
                <Typography variant="body1lanna" sx={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}>
                  {description}
                </Typography>
              </Box>
            </Box>

            {children && <Box sx={{ marginTop: '16px' }}>{children}</Box>}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            gap: isOnCancel ? '16px' : '24px',
            justifyContent: 'center',
          }}
        >
          {isOnCancel && (
            <Button
              sx={{ padding: '6px 24px', minWidth: { xs: '100%', md: '120px' } }}
              variant="outlined"
              onClick={handleCancel}
            >
              {t(cancelText || DEFAULT_CANCEL_TEXT)}
            </Button>
          )}
          <Button
            sx={{
              padding: isOnCancel ? '6px 24px' : '6px 40px',
              width: { xs: '100%', md: fullWidthConfirmButton ? '100%' : '120px' },
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            {t(confirmText || DEFAULT_CONFIRM_TEXT)}
          </Button>
        </DialogActions>
      </Box>
    </BaseModalWrapper>
  );
};

export default BaseModalAlertWrapper;
